import * as m from '@/paraglide/messages';
import { cn } from '@listening/utils/react-helpers';
import { SettingsRowsSkeleton } from '@/components/web-fe/design-v2/skeletons';
import {
  SettingsCard,
  SettingsPaneHeader,
} from '@/components/web-fe/design-v2/settings-shell';
import {
  primaryButtonSurface,
  secondaryButtonSurface,
} from '@/components/web-fe/design-v2/surfaces';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Check, Link, OpenBook, RefreshDouble } from 'iconoir-react';
import { useEffect } from 'react';
import { Schema } from 'effect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  disconnectZoteroEffect,
  zoteroKeys,
  zoteroStatusEffect,
} from '@listening/frontend/query/zotero';
import { eq } from '@/runtimes/effect-query';
import { authClient } from '@/lib/auth-client';
import { clientEnv } from '@/env';

// TanStack Router's default search parser runs JSON.parse on each value, so
// `?connected=true` arrives as a boolean. Typing it as such keeps the redirect
// URL unchanged while letting the route schema validate cleanly.
const searchSchema = Schema.toStandardSchemaV1(
  Schema.Struct({
    connected: Schema.optional(Schema.Boolean),
    error: Schema.optional(Schema.String),
  }),
);

export const Route = createFileRoute(
  '/app/_authenticated/_dashboard/settings/integrations',
)({
  component: IntegrationsPage,
  pendingComponent: SettingsRowsSkeleton,
  pendingMs: 0,
  validateSearch: searchSchema,
  beforeLoad: async ({ search }) => {
    if (!clientEnv.zoteroUiEnabled) {
      throw redirect({ to: '/app/settings' });
    }
    // Post-OAuth callback: force a fresh session fetch so `enabledIntegrations`
    // reflects the new connection before the page paints (the 5-min cookie
    // cache would otherwise mask the new state).
    if (search.connected === true) {
      await authClient.getSession({ query: { disableCookieCache: true } });
    }
  },
  context: () => ({
    title: m.settings_integrations_title(),
    icon: Link,
  }),
});

function IntegrationsPage() {
  const { connected, error } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const statusQuery = useQuery(
    eq.queryOptions({
      queryKey: zoteroKeys.status,
      queryFn: () => zoteroStatusEffect,
    }),
  );

  const disconnectMutation = useMutation(
    eq.mutationOptions({
      mutationFn: () => disconnectZoteroEffect,
      onSuccess: async () => {
        void queryClient.invalidateQueries({ queryKey: zoteroKeys.status });
        // Refresh session so the home-screen Zotero upload card (gated on
        // `user.enabledIntegrations`) hides immediately.
        await authClient.getSession({ query: { disableCookieCache: true } });
        toast.success(m.settings_integrations_zotero_disconnect_success());
      },
      onError: () => {
        toast.error(m.settings_integrations_zotero_disconnect_error());
      },
    }),
  );

  useEffect(() => {
    if (!connected && !error) return;
    if (connected === true) {
      toast.success(m.settings_integrations_zotero_connect_success());
    }
    if (error) {
      toast.error(
        m.settings_integrations_zotero_connect_error({ reason: error }),
      );
    }
    void navigate({
      to: '/app/settings/integrations',
      search: {},
      replace: true,
    });
  }, [connected, error, navigate]);

  const status = statusQuery.data;
  const loading = statusQuery.isPending;

  return (
    <>
      <SettingsPaneHeader title={m.settings_integrations_title()} />
      <SettingsCard>
        <div className='flex items-center justify-between gap-[var(--v2-gap-4)] p-[var(--v2-gap-4)]'>
          <div className='flex items-center gap-3'>
            <div className='flex size-10 shrink-0 items-center justify-center rounded-[var(--v2-button-radius)] bg-[var(--v2-card-bg)]'>
              <OpenBook className='size-5 text-[var(--v2-text-muted)]' />
            </div>
            <div className='flex min-w-0 flex-col gap-1'>
              <span
                className='text-[15px] text-[var(--v2-text-primary)]'
                style={{ fontWeight: 'var(--v2-heading-weight)' }}
              >
                {m.settings_integrations_zotero_title()}
              </span>
              <span className='text-[13px] text-[var(--v2-text-muted)]'>
                {m.settings_integrations_zotero_description()}
              </span>
              {status?.connected && (
                <span className='flex items-center gap-1 text-xs text-green-600 dark:text-green-400'>
                  <Check className='size-3' />
                  {status.display_name
                    ? m.settings_integrations_zotero_connected_as({
                        displayName: status.display_name,
                      })
                    : m.settings_integrations_zotero_connected()}
                </span>
              )}
            </div>
          </div>
          <div className='shrink-0'>
            {loading ? (
              <RefreshDouble className='size-5 animate-spin text-[var(--v2-text-muted)]' />
            ) : status?.connected ? (
              <button
                type='button'
                className={cn(
                  secondaryButtonSurface,
                  'flex h-9 items-center px-3.5 text-[13px] font-medium',
                )}
                onClick={() => {
                  disconnectMutation.mutate(undefined);
                }}
                disabled={disconnectMutation.isPending}
              >
                {disconnectMutation.isPending ? (
                  <RefreshDouble className='size-4 animate-spin' />
                ) : (
                  m.settings_integrations_zotero_disconnect()
                )}
              </button>
            ) : (
              <button
                type='button'
                className={cn(
                  primaryButtonSurface,
                  'flex h-9 items-center px-3.5 text-[13px] font-medium',
                )}
                onClick={() => {
                  // Browser navigation: the connect endpoint sets a signed
                  // cookie then 302-redirects to Zotero's authorize page.
                  window.location.href = '/api/v2/integrations/zotero/connect';
                }}
              >
                {m.settings_integrations_zotero_connect()}
              </button>
            )}
          </div>
        </div>
      </SettingsCard>
    </>
  );
}
